
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  Attachment, AttachmentType, AttachmentTypeOptions, ChildLeads,
  Lead, LeadStatus, PermissionCode, ProjectsPool, Task, TaskStatus,
} from '@/types';
import AttachmentTypeComponent from '@/components/AttachmentType.vue';
import AttachmentDate from '@/components/AttachmentDate.vue';
import AttachmentSize from '@/components/AttachmentSize.vue';
import AttachmentsGallery from '@/components/AttachmentsGallery.vue';
import AttachmentsUploadDialog from '@/components/AttachmentsUploadDialog.vue';
import AttachmentDeleteDialog from '@/components/AttachmentDeleteDialog.vue';
import AttachmentSelectDialog from '@/components/AttachmentSelectDialog.vue';
import { currentUserHasPermission, currentUserIsSuper } from '@/helpers';
import { matchesAllWords } from '@/helpers/textSearch';
import AttachmentTypeDialog from '@/components/AttachmentTypeDialog.vue';

@Component({
  components: {
    AttachmentTypeComponent,
    AttachmentDate,
    AttachmentSize,
    AttachmentsGallery,
    AttachmentsUploadDialog,
    AttachmentDeleteDialog,
    AttachmentSelectDialog,
    AttachmentTypeDialog,
  },
})
export default class AttachmentsList extends Vue {
  @Prop({ type: [String, Array], default: null }) readonly type!: AttachmentType | AttachmentType[] | null
  @Prop(Object) readonly task: Task | undefined
  @Prop(String) readonly title: string | undefined
  @Prop({ type: Boolean, default: false }) readonly editable!: boolean
  @Prop({ type: Boolean, default: false }) readonly deletable!: boolean
  @Prop({ type: Boolean, default: false }) readonly showType!: boolean
  @Prop({ type: Boolean, default: false }) readonly showDate!: boolean
  @Prop({ type: Boolean, default: false }) readonly showSize!: boolean
  @Prop({ type: Boolean, default: false }) readonly showFilters!: boolean
  @Prop({ type: Boolean, default: false }) readonly showEmpty!: boolean
  @Prop({ type: Boolean, default: false }) readonly useFather!: boolean

  @Prop({ type: Boolean, default: false }) readonly required!: boolean
  @Prop({ type: String, default: 'Errore' }) readonly errorMessage!: boolean

  // Se true, sarà possibile soltanto scaricare e vedere gli allegati
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  // Se true, è utilizzato all'interno di una Connection
  @Prop({ type: Boolean, default: false }) readonly connection!: boolean

  // Se specificato, consente la selezione di un allegato esistente dei tipi selezionati
  @Prop({ type: Array, default: () => [] }) readonly selectFrom!: AttachmentType[]

  // Se true, il componente emette soltanto gli eventi di aggiunta e rimozione senza chiamare le API
  @Prop({ type: Boolean, default: false }) readonly standalone!: boolean

  // Se true, il componente non mostra mai gli allegati
  @Prop({ type: Boolean, default: false }) readonly hidden!: boolean

  // Se true, esclude gli allegati del padre quando si visualizza una lead figlia
  @Prop({ type: Boolean, default: false }) readonly excludeParentAttachments!: boolean

  // Se specificato, filtra gli allegati per mostrare solo quelli della Lead con questo id
  @Prop({ type: Number, default: null }) readonly filterLeadId!: number | null

  // Se specificato (e incluso nell'array `type`), il pulsante "Aggiungi" principale
  // aggiunge direttamente questo tipo di allegato, con un menu secondario per gli altri tipi.
  @Prop({ type: String, default: null }) readonly preferredType!: AttachmentType | null

  uploadDialog = false
  uploadDialogType: AttachmentType | null = null
  deleteDialog = false
  selectDialog = false
  galleryDialog = false
  changeTypeDialog = false;

  downloading = false

  filters = {
    search: '',
    type: null,
  }

  selected: Attachment | null = null

  addedAttachments: Attachment[] = [] // Nuovi allegati da aggiungere
  deletedAttachmentsId: number[] = [] // Id degli allegati da rimuovere
  selectedAttachments: Attachment[] = [] // Id degli allegati selezionati (da duplicare)

  get isSuper(): boolean { return currentUserIsSuper(); }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get father(): Lead | null {
    if (!this.task || 'fatherLeadId' in this.task === false) return null;
    const fatherLeadId = this.task.relationship === 'child'
      ? this.task.fatherLeadId : this.task.LeadId;

    return fatherLeadId && fatherLeadId in this.pool
      ? this.pool[fatherLeadId]
      : null;
  }

  get status(): LeadStatus | null {
    return this.lead?.status;
  }

  get taskStatus(): TaskStatus {
    return this.task?.status ?? null;
  }

  get childLeads(): ChildLeads {
    return this.$store.getters.currentChildren;
  }

  get realLeadId(): number {
    // Se è una connessione ed è richiesto l'utilizzo della lead padre,
    // ritorna l'id della connessione che corrissponde a quello della lead padre.
    if (this.connection
      && this.useFather
      && this.$store.getters.currentConnection?.id
    ) return this.$store.getters.currentConnection.id;

    if (this.useFather && this.father) return this.father.id;

    if (this.task && this.task.relationship === 'father') {
      // È un task associato ad una lead figlia

      // Se la lead figlia non è stata ancora assegnata,
      // non ritorna nessun allegato.
      if ('childLeadId' in this.task === false || !this.task.childLeadId) return this.lead.id;

      // Se la lead figlia è stata assegnata,
      // considera i suoi allegati.
      return this.task.childLeadId in this.childLeads && this.childLeads[this.task.childLeadId]
        ? this.childLeads[this.task.childLeadId].id
        : this.lead.id;
    }

    // Se è una connessione, ottiene l'id della lead figlia
    if (this.connection
      && this.$store.getters.currentConnection
      && 'eChildLeadId' in this.$store.getters.currentConnection
      && this.$store.getters.currentConnection.eChildLeadId) {
      return this.$store.getters.currentConnection.eChildLeadId;
    }

    return this.lead.id;
  }

  // get attachments(): Attachment[] {
  //   let { lead } = this;

  //   if (this.useFather && this.father) {
  //     lead = this.father;
  //   } else
  //   if (this.task && this.task.relationship === 'father') {
  //     // È un task associato ad una lead figlia

  //     // Se la lead figlia non è stata ancora assegnata,
  //     // non ritorna nessun allegato.
  //     if ('childLeadId' in this.task === false || !this.task.childLeadId) return [];

  //     // Se la lead figlia è stata assegnata,
  //     // considera i suoi allegati.
  //     lead = this.childLeads[this.task.childLeadId];
  //   }

  //   if (!lead || !Array.isArray(lead.Attachments)) return [];

  //   if (this.type) {
  //     return lead.Attachments.filter((attachment: Attachment) => attachment.fieldname === this.type)
  //       ?? [];
  //   }
  //   return lead.Attachments ?? [];
  // }

  // Helper per verificare se un tipo di allegato è incluso nel prop type
  isTypeIncluded(attachmentType: AttachmentType): boolean {
    // Se non è stato specificato un tipo, mostra tutti gli allegati
    if (!this.type) return true;

    // Se è stato specificato un array di tipi, verifica se il tipo è incluso nell'array
    if (Array.isArray(this.type)) {
      return this.type.includes(attachmentType);
    }

    // Se è stato specificato un tipo singolo, verifica se il tipo è uguale al tipo specificato
    return this.type === attachmentType;
  }

  // Tutti gli allegati associati alla Lead e alle sue eventuali figlie
  get attachments(): Attachment[] {
    if (!this.lead || !Array.isArray(this.$store.getters.currentLeadAttachments)) return [];

    let attachments = this.$store.getters.currentLeadAttachments;

    // Se excludeParentAttachments è true e siamo su una lead figlia,
    // filtra solo gli allegati che appartengono alla lead corrente
    if (this.excludeParentAttachments && ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.lead.type)) {
      attachments = attachments.filter((attachment: Attachment) => attachment.LeadId === this.lead.id);
    }

    // Se filterLeadId è specificato, filtra per mostrare solo gli allegati di quella lead
    if (this.filterLeadId != null) {
      attachments = attachments.filter((attachment: Attachment) => attachment.LeadId === this.filterLeadId);
    }

    return !this.type
      ? attachments
      : attachments.filter((attachment: Attachment) => this.isTypeIncluded(attachment.fieldname));
  }

  get noAttachments() {
    return !this.filteredAttachments
      || (Array.isArray(this.filteredAttachments) && this.filteredAttachments.length === 0);
  }

  get filteredAttachments() {
    const attachmentList: Attachment[] = [
      ...this.addedAttachments,
      ...this.selectedAttachments,
      ...this.attachments,
    ];

    const uniqueAttachmentList = attachmentList
      .reduce((uniqueAttachments: Attachment[], currentAttachment: Attachment) => {
        const existingAttachment = uniqueAttachments
          .find((attachment) => attachment.id === currentAttachment.id);

        if (!existingAttachment) {
          uniqueAttachments.push(currentAttachment);
        }

        return uniqueAttachments;
      }, []);

    return uniqueAttachmentList.filter((a: Attachment) => {
      // Nasconde gli allegati eliminati (non confermati)
      if (this.deletedAttachmentsId.includes(a.id)) return false;

      // Nasconde gli alleggati che l'utente non può visualizzare
      if (!this.hasPermission(`attachments.${a.fieldname}.read`)) return false;

      // Filtra per tipologia
      if (this.filters.type && a.fieldname !== this.filters.type) return false;

      // Filtra eventualmente per il testo di ricerca
      return matchesAllWords(this.filters.search, [a.originalname, a.fieldname]);
    });
  }

  get typeOptions() {
    let options = Object.entries(AttachmentTypeOptions)
      .map((option) => ({ value: option[0], text: option[1] }))
      .filter((option) => this.hasPermission(`attachments.${option.value}.read`));

    // Se type è un array, mostra solo i tipi inclusi nell'array
    if (Array.isArray(this.type) && this.type.length > 0) {
      const typeArray = this.type;
      options = options.filter((option) => typeArray.includes(option.value as AttachmentType));
    }

    return options;
  }

  /** Tipo passato a AttachmentsUploadDialog: sempre singolo o null, mai array. */
  get uploadDialogTypeProp(): AttachmentType | null {
    if (this.uploadDialogType !== null) return this.uploadDialogType;
    if (Array.isArray(this.type)) return null;
    return this.type as AttachmentType | null;
  }

  get addTypeOptions(): { value: AttachmentType; text: string }[] {
    if (!Array.isArray(this.type) || this.type.length === 0) return [];
    return this.type
      .filter((t) => this.hasPermission(`attachments.${t}.add`))
      .map((t) => ({
        value: t,
        text: t in AttachmentTypeOptions ? AttachmentTypeOptions[t] : t,
      }));
  }

  get hasValidPreferredType(): boolean {
    if (!this.preferredType) return false;
    if (!Array.isArray(this.type)) return false;
    if (!this.type.includes(this.preferredType)) return false;
    return this.hasPermission(`attachments.${this.preferredType}.add`);
  }

  get preferredTypeLabel(): string {
    if (!this.preferredType) return '';
    return this.preferredType in AttachmentTypeOptions
      ? AttachmentTypeOptions[this.preferredType]
      : this.preferredType;
  }

  get visible() {
    if (this.type !== null) {
      // Se è stata specificata una tipologia o un array di tipologie,
      // verifica che l'utente abbia il permesso per elencare almeno una.
      if (Array.isArray(this.type)) {
        // Se è un array, almeno un tipo deve essere leggibile
        if (this.type.length === 0 || !this.type.some((t) => this.hasPermission(`attachments.${t}.read`))) {
          return false;
        }
      }
      // Se è una stringa singola, verifica il permesso
      if (!Array.isArray(this.type) && !this.hasPermission(`attachments.${this.type}.read`)) {
        return false;
      }
    }
    // Se non è specificata una tipologia, dovrà filtrarle una ad una.

    // Mostra il componente se ci sono allegati ma anche se non ci sono nel caso
    // sia stato specificato il parametro showEmpty.
    return !this.noAttachments || this.showEmpty;
  }

  get canAdd() {
    // Se il componente è in modalità sola lettura,
    // nessuno può aggiungere allegati.
    if (this.readonly) return false;

    // Se non è stato specificato 'editable' non è possibile aggiungere
    // a meno che l'utente non sia un SuperUser.
    if (!this.editable && !this.isSuper) return false;

    // Se è stato specificato il 'type' (singolo o array) e l'utente ha il permesso di
    // aggiungere allegati di almeno un tipo, lo consente.
    if (this.type !== null) {
      if (Array.isArray(this.type)) {
        // Se è un array, almeno un tipo deve essere aggiungibile
        return this.type.length > 0 && this.type.some((t) => this.hasPermission(`attachments.${t}.add`));
      }
      // Se è una stringa singola, verifica il permesso
      return this.hasPermission(`attachments.${this.type}.add`);
    }

    // Se non è stato specificato il 'type' e l'utente ha il permesso di
    // aggiungere allegati di tipo 'altri', lo consente.
    if (this.type === null && this.hasPermission('attachments.altri.add')) return true;

    return false;
  }

  get canDelete() {
    // Se il componente è in modalità sola lettura,
    // nessuno può eliminare allegati.
    if (this.readonly) return false;

    // Se non è stato specificato 'editable' non è possibile aggiungere
    if (!this.deletable && !this.isSuper) return false;

    // Se è stato specificato il 'type' (singolo o array) e l'utente ha il permesso di
    // eliminare allegati di almeno un tipo, lo consente.
    if (this.type !== null) {
      if (Array.isArray(this.type)) {
        // Se è un array, almeno un tipo deve essere eliminabile
        return this.type.length > 0 && this.type.some((t) => this.hasPermission(`attachments.${t}.delete`));
      }
      // Se è una stringa singola, verifica il permesso
      return this.hasPermission(`attachments.${this.type}.delete`);
    }

    // Se non è stato specificato il 'type' e l'utente ha il permesso di
    // eliminare allegati di tipo 'altri', lo consente.
    if (this.type === null && this.hasPermission('attachments.altri.delete')) return true;

    return false;
  }

  get canChangeType() {
    // Se il componente è in modalità sola lettura,
    // nessuno può modificare la categoria degli allegati.
    if (this.readonly) return false;

    // Consente la modifica del tipo di allegato soltanto se non ne è stato specificato uno (scheda Allegati)
    // o se è stato specificato un array vuoto
    if (this.type !== null && !(Array.isArray(this.type) && this.type.length === 0)) return false;

    // Se non è stato specificato 'editable' non è possibile fare modifiche
    if (!this.deletable && !this.isSuper) return false;

    return this.hasPermission('attachments.changeType');
  }

  // L'utente può selezionare un allegato esistente
  get canSelect() {
    return this.selectFrom.length > 0;
  }

  get selectFromTypes(): string {
    const attachmentTypes = this.selectFrom.map((a) => (`${a}` in AttachmentTypeOptions ? AttachmentTypeOptions[`${a}`] : 'Sconosciuto'));
    return attachmentTypes.join(', ');
  }

  // Tipo da passare a AttachmentSelectDialog (deve essere una stringa, non un array)
  get selectDialogType(): AttachmentType | null {
    if (!this.type) return null;
    if (Array.isArray(this.type)) {
      // Se type è un array, restituisce il primo elemento o null se l'array è vuoto
      return this.type.length > 0 ? this.type[0] : null;
    }
    // Se type è una stringa, la restituisce direttamente
    return this.type;
  }

  get areButtonsEnabled() {
    // Se ancora non conosco lo stato della lead, il pulsante è disattivato.
    if (!this.status) return false;

    // Se il componente è in modalità sola lettura, il pulsante è disattivato.
    if (this.readonly) return false;

    // Per i super utenti, il pulsante è sempre attivo
    if (this.isSuper) return true;

    // Se la lead è aperta o nuova, il pulsante è attivo.
    if (['new', 'open'].includes(this.status)) return true;

    // In tutti gli altri casi il pulsante è disattivato
    return false;
  }

  // Altezza in pixel della tabella degli allegati
  get tableHeight(): string | undefined {
    // Quando gli allegati sono più di 5
    // e non siamo nell'elenco di tutti gli allegati (type === null o array vuoto),
    // limita l'altezza massima in pixel della tabella.
    const hasType = this.type !== null && !(Array.isArray(this.type) && this.type.length === 0);
    return !this.attachments
      || !hasType
      || (Array.isArray(this.attachments) && this.attachments.length < 5)
      ? undefined
      : '290px';
  }

  // È necessario salvare le modifiche al Task/Connection?
  get dirty(): boolean {
    if (this.connection) return this.$store.getters.isConnectionDirty;
    return this.$store.getters.isTaskDirty;
  }

  get error(): boolean {
    return this.required && this.noAttachments;
  }

  // Disattiva il pulsante "Scarica tutti" se sono elencati
  // nuovi allegati non ancora memorizzati (non verrebbero scaticati).
  get downloadAllDisabled(): boolean {
    return this.selectedAttachments.length > 0
      || [
        ...this.addedAttachments
          .filter((a) => !this.deletedAttachmentsId.includes(a.id)),
      ].length > 0;
  }

  @Watch('dirty')
  onDirtyChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onDirtyChange() ${oldVal} -> ${val}`); // eslint-disable-line no-console

    // Questa pulizia ha senso solo se stiamo modificando gli allegati di un Task
    // o di una Connection (salvataggio delle modifiche differito).
    if (!this.task && !this.connection) return;

    // Se le modifiche del task sono state aggiornate...
    if (val === false && oldVal === true) {
      console.log(`${this.constructor.name}: type = ${this.type} onDirtyChange() svuota gli elenchi che tengono traccia degli allegati aggiunti e rimossi`); // eslint-disable-line no-console

      setTimeout(() => {
        console.log(`${this.constructor.name}: onDirtyChange() timeout!`); // eslint-disable-line no-console

        // ... svuota gli elenchi che tengono traccia degli allegati aggiunti e rimossi
        this.addedAttachments = [];
        this.deletedAttachmentsId = [];
        this.selectedAttachments = [];
      }, 2000);
    }
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  // Se l'allegato è tra quelli aggiunti o selezionati, lo considera come nuovo
  isNew(attachment: Attachment) {
    return [...this.addedAttachments, ...this.selectedAttachments]
      .findIndex((a) => a.id === attachment.id) > -1;
  }

  onAddAttachments(newAttachments: Attachment[]) {
    console.log(`${this.constructor.name}: onAddAttachments() newAttachments =`, newAttachments); // eslint-disable-line no-console

    // Sono stati aggiunti uno o più allegati

    // Aggiunge gli allegati all'elenco di quelli aggiunti (per mostrarli nella tabella)
    this.addedAttachments.push(...newAttachments);

    // Se il componente è in modalità standalone...
    if (this.standalone) {
      // ... emette l'evento di aggiunta
      this.$emit('add', newAttachments);
    } else {
      // ... altrimenti, aggiunge gli allegati all'elenco di quelli da aggiungere nel prossimo eventuale salvataggio

      // FIXME: Unificare in una chiamata sola, gestita in `store/modules/leads.ts`
      // eslint-disable-next-line no-lonely-if
      if (this.connection) {
        this.$store.dispatch('connectionAttachments', {
          action: 'add',
          attachments: newAttachments,
        });
      } else {
        this.$store.dispatch('taskAttachments', {
          action: 'add',
          attachments: newAttachments,
        });
      }
    }
  }

  onSelectAttachments(selectedAttachments: Attachment[]) {
    console.log(`${this.constructor.name}: onSelectAttachments() selectedAttachments =`, selectedAttachments); // eslint-disable-line no-console

    this.selectedAttachments.push(...selectedAttachments);

    // FIXME: Unificare in una chiamata sola, gestita in `store/modules/leads.ts`
    if (this.connection) {
      this.$store.dispatch('connectionAttachments', {
        action: 'select',
        type: this.type,
        attachments: selectedAttachments,
      });
    } else {
      this.$store.dispatch('taskAttachments', {
        action: 'select',
        type: this.type,
        attachments: selectedAttachments,
      });
    }
  }

  onDeleteAttachment(id: number) {
    console.log(`${this.constructor.name}: onDeleteAttachment(${id})`); // eslint-disable-line no-console

    // È stato eliminato un allegato

    // Se l'id dell'allegato cancellato è tra quelli selezionati per essere duplicati...
    if (this.selectedAttachments.map((a) => a.id).includes(id)) {
      // e lo rimuove dall'elenco
      this.selectedAttachments = this.selectedAttachments.filter((sa) => sa.id !== id);

      // Se il componente è in modalità standalone...
      if (this.standalone) {
        // ... emette l'evento di eliminazione
        this.$emit('delete', id);
      } else {
        // ... altrimenti, deseleziona l'allegato dall'elenco di quelli da duplicare nel prossimo eventuale salvataggio

        // FIXME: Unificare in una chiamata sola, gestita in `store/modules/leads.ts`
        // eslint-disable-next-line no-lonely-if
        if (this.connection) {
          this.$store.dispatch('connectionAttachments', {
            action: 'unselect',
            type: this.type,
            attachmentId: id,
          });
        } else {
          this.$store.dispatch('taskAttachments', {
            action: 'unselect',
            type: this.type,
            attachmentId: id,
          });
        }
      }
    } else {
      // Se appartiene all'elenco di quelli aggiunti ma non memorizzati...
      if (this.addedAttachments.findIndex((a) => a.id === id) > -1) {
        // ... lo rimuove dall'elenco
        this.addedAttachments = this.addedAttachments.filter((a) => a.id !== id);
      } else {
        // altrimenti, tiene l'id memorizzato così può filtrarlo dall'elenco visualizzato
        this.deletedAttachmentsId.push(id);
      }

      // Se il componente è in modalità standalone...
      if (this.standalone) {
        // ... emette l'evento di eliminazione
        this.$emit('delete', id);
      } else {
        // ... altrimenti, aggiunge l'id dell'allegato all'elenco di quelli da eliminare nel prossimo eventuale salvataggio

        // FIXME: Unificare in una chiamata sola, gestita in `store/modules/leads.ts`
        // eslint-disable-next-line no-lonely-if
        if (this.connection) {
          this.$store.dispatch('connectionAttachments', {
            action: 'delete',
            attachmentId: id,
          });
        } else {
          this.$store.dispatch('taskAttachments', {
            action: 'delete',
            attachmentId: id,
          });
        }
      }
    }
  }

  onChangeAttachmentType(id: number, type: string) {
    console.log(`${this.constructor.name}: onChangeAttachmentType(${id}): ${type}`); // eslint-disable-line no-console

    // TODO: da implementare
    this.$store.dispatch('updateAttachmentType', {
      attachment: id,
      type,
    });
  }

  onChangeType(attachment: Attachment) {
    this.selected = attachment;
    this.changeTypeDialog = true;
  }

  onView(attachment: Attachment) {
    this.selected = attachment;
    this.galleryDialog = true;
  }

  onDownload(attachment: Attachment) { window.location.href = `/download/${attachment.filename}`; }

  onDownloadAll() {
    this.downloading = true;
    // Se l'utente ha selezionato un tipo nel filtro, scarica solo quello
    if (this.filters.type) {
      window.location.href = `/zip/${this.realLeadId}/${this.filters.type}`;
    } else if (Array.isArray(this.type) && this.type.length > 0) {
      // Se type è un array, usa l'endpoint per tipi multipli
      const typesParam = this.type.join(',');
      window.location.href = `/zip/${this.realLeadId}?types=${encodeURIComponent(typesParam)}`;
    } else if (this.type) {
      window.location.href = `/zip/${this.realLeadId}/${this.type}`;
    } else {
      window.location.href = `/zip/${this.realLeadId}/all`;
    }
    setTimeout(() => {
      this.downloading = false;
    }, 2000);
  }

  @Watch('uploadDialog')
  onUploadDialogChange(val: boolean) {
    if (val === false) this.uploadDialogType = null;
  }

  onAdd(selectedType?: AttachmentType) {
    if (selectedType !== undefined) {
      this.uploadDialogType = selectedType;
    }
    this.uploadDialog = true;
  }

  onDelete(attachment: Attachment) {
    this.selected = attachment;
    this.deleteDialog = true;
  }

  onSelect() { this.selectDialog = true; }
}
